import React, { useState, useRef, useEffect, JSX } from 'react';
import { Box, Textarea, Button, IconButton, Image, Text, Input, Spinner, Stack, Heading, Flex, Link, createToaster } from '@chakra-ui/react';
import { FiSend, FiCamera, FiImage, FiInfo, FiSearch, FiTool, FiArrowLeft, FiMessageCircle, FiTrendingUp, FiX, FiExternalLink } from 'react-icons/fi';
import { resizeImage } from './utils/imageUtils';
import FeedbackModal from './components/FeedbackModal';
// import { useAuth } from '../UserManagement/authprovider';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './DIYBuddy.css';
import './markdown.css';
import { trackButtonClick, trackNavigation, DIY_OPTION_LABELS, DIY_OPTIONS } from '../utils/posthog';
import posthog from 'posthog-js';

// Add CSS for markdown styling in DIYBuddy.css file

// Define navigation states for the app
type NavigationState = 'issueOptions' | 'issueChat';

interface ChatMessage {
  id: string;
  type: 'user' | 'ai';
  text?: string;
  imageUrl?: string;
  imageUrls?: string[];
  createdAt: string;
  isMarkdown?: boolean;
}

interface ChatOptionsProps {
  handleOptionSelect: (option: string) => void;
  onOpenFeedbackModal: () => void;
}

const ChatOptions: React.FC<ChatOptionsProps> = ({ handleOptionSelect, onOpenFeedbackModal }) => {
  return (
    <Stack mt={4} mb={4} gap={4} alignItems="stretch">
      <Heading size="md" textAlign="center" mb={2}>How can I help you today?</Heading>
      <Button 
        variant="outline" 
        size="lg"
        height="auto"
        minHeight="80px"
        justifyContent="flex-start"
        onClick={() => handleOptionSelect('diagnose')}
        borderColor="var(--brand-secondary)"
        color="var(--gray-700)"
        _hover={{ bg: 'var(--brand-subtle)' }}
        transition="all 0.2s ease"
        p={4}
        whiteSpace="normal"
        textAlign="left"
      >
        <Flex minWidth="24px" mr={3} alignSelf="flex-start" mt={1}><FiTool /></Flex>
        <Box>
          <Text fontWeight="medium">Plan a DIY project or get help with a repair</Text>
          <Text fontSize="sm" mt={1} fontWeight="normal" color="var(--gray-600)">Get step-by-step guidance for home projects, troubleshooting, or repairing appliances and issues around the house.</Text>
        </Box>
      </Button>
      <Button 
        variant="outline" 
        size="lg" 
        height="auto"
        minHeight="80px"
        justifyContent="flex-start"
        onClick={() => handleOptionSelect('applianceInstructions')}
        whiteSpace="normal"
        textAlign="left"
        borderColor="var(--brand-secondary)"
        color="var(--gray-700)"
        _hover={{ bg: 'var(--brand-subtle)' }}
        transition="all 0.2s ease"
        p={4}
      >
        <Flex minWidth="24px" mr={3} alignSelf="flex-start" mt={1}><FiInfo /></Flex>
        <Box>
          <Text fontWeight="medium">Give me information or instructions for an appliance</Text>
          <Text fontSize="sm" mt={1} fontWeight="normal" color="var(--gray-600)">Get general guidance, or instructions on how to use your appliance.</Text>
        </Box>
      </Button>
      <Button 
        variant="outline" 
        size="lg"
        height="auto"
        minHeight="80px"
        justifyContent="flex-start"
        onClick={() => handleOptionSelect('identifyPart')}
        borderColor="var(--brand-secondary)"
        color="var(--gray-700)"
        _hover={{ bg: 'var(--brand-subtle)' }}
        transition="all 0.2s ease"
        p={4}
        whiteSpace="normal"
        textAlign="left"
      >
        <Flex minWidth="24px" mr={3} alignSelf="flex-start" mt={1}><FiSearch /></Flex>
        <Box>
          <Text fontWeight="medium">Help me identify a part</Text>
          <Text fontSize="sm" mt={1} fontWeight="normal" color="var(--gray-600)">Identify replacement parts for appliances or issues around the house.</Text>
        </Box>
      </Button>
      <Flex justifyContent="flex-end" mt={2}>
        <Button
          variant="ghost"
          size="sm"
          onClick={onOpenFeedbackModal}
          color="var(--gray-600)"
          _hover={{ color: 'var(--brand-secondary)' }}
        >
          <FiMessageCircle style={{ marginRight: '8px' }} /> Send Feedback
        </Button>
      </Flex>
    </Stack>
  );
}

interface ChatHistoryProps {
  messages: ChatMessage[];
  isSubmitting: boolean;
  chatBottomRef: React.RefObject<HTMLDivElement | null>;
}

const ChatHistory: React.FC<ChatHistoryProps> = ({ messages, isSubmitting, chatBottomRef }) => {
  // Colors from our amber palette
  const bgBubbleUser = 'var(--brand-secondary)'; // amber.500
  const bgBubbleAI = 'var(--amber-50)'; // amber.50
  
  // Debug: log messages to see what's happening (commented out for production)
  // console.log('Messages:', messages.length, messages.map(m => m.type))
  
  // Function to render messages with ad pinned after 2nd message
  const renderMessagesWithAd = () => {
    const elements: JSX.Element[] = [];
    
    messages.forEach((message, index) => {
      // Add the message
      elements.push(
        <Box
          key={message.id}
          alignSelf={message.type === 'user' ? 'flex-end' : 'flex-start'}
          maxWidth="80%"
          bg={message.type === 'user' ? bgBubbleUser : bgBubbleAI}
          color={message.type === 'user' ? 'white' : 'black'}
          borderRadius={message.type === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px'}
          px={4}
          py={3}
          position="relative"
          className={message.type === 'user' ? 'user-message' : 'ai-message'}
        >
          {message.text && message.isMarkdown ? (
            <Box className="markdown-content" color={message.type === 'user' ? 'white' : 'black'}>
              <ReactMarkdown 
                remarkPlugins={[remarkGfm]}
                components={{
                  a: ({node, ...props}) => (
                    <Link 
                      color={message.type === 'user' ? 'blue.100' : 'blue.500'} 
                      href={props.href || '#'} 
                      display="inline-flex" 
                      alignItems="center"
                      _hover={{ textDecoration: 'underline' }}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {props.children}
                      <FiExternalLink style={{ marginLeft: '4px', fontSize: '0.75em' }} />
                    </Link>
                  ),
                  p: ({node, ...props}) => <Text mb={2}>{props.children}</Text>,
                  ul: ({node, ...props}) => <Box as="ul" pl={4} mb={2}>{props.children}</Box>,
                  ol: ({node, ...props}) => <Box as="ol" pl={4} mb={2}>{props.children}</Box>,
                  li: ({node, ...props}) => <Box as="li" mb={1}>{props.children}</Box>,
                  code: ({node, ...props}) => {
                    const isInline = !(props.className?.includes('language-'));
                    return (
                      <Box 
                        as="code" 
                        bg={message.type === 'user' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.05)'} 
                        p={isInline ? '0.2em 0.4em' : 2}
                        borderRadius="md"
                        fontFamily="monospace"
                        display={isInline ? 'inline' : 'block'}
                        whiteSpace="pre-wrap"
                        overflowX="auto"
                      >
                        {props.children}
                      </Box>
                    );
                  }
                }}
              >
                {message.text}
              </ReactMarkdown>
            </Box>
          ) : message.text && (
            <Text whiteSpace="pre-wrap">{message.text}</Text>
          )}
          {message.imageUrls && message.imageUrls.length > 0 && (
            <Box display="flex" flexDirection="row" flexWrap="wrap" gap={2} mt={message.text ? 2 : 0}>
              {message.imageUrls.map((url, idx) => (
                <Image 
                  key={idx}
                  src={url} 
                  alt={`Uploaded image ${idx + 1}`} 
                  borderRadius="8px" 
                  maxHeight="200px"
                  maxWidth="200px"
                  objectFit="cover"
                />
              ))}
            </Box>
          )}
          {/* For backward compatibility */}
          {message.imageUrl && !message.imageUrls && (
            <Image 
              src={message.imageUrl} 
              alt="Uploaded image" 
              borderRadius="8px" 
              mt={message.text ? 2 : 0} 
              maxHeight="200px"
            />
          )}
        </Box>
      );
      
      // Insert ad after the 2nd message (index 1) if we have at least 2 messages
      if (index === 1 && messages.length >= 2) {
        elements.push(
          <div
            key="pinned-ad"
            style={{
              alignSelf: 'center',
              width: '100%',
              maxWidth: '300px',
              margin: '8px 0',
            }}
            ta-ad-container=""
          />
        );
      }
    });
    
    return elements;
  };

  return (
    <Box
      flex={1}
      overflowY="auto"
      px={4}
      py={2}
      display="flex"
      flexDirection="column"
      gap={4}
    >
      {renderMessagesWithAd()}
      
      {isSubmitting && (
        <Box alignSelf="flex-start" bg={bgBubbleAI} borderRadius="16px 16px 16px 4px" px={4} py={3}>
          <Spinner size="sm" mr={2} />
          <Text as="span">Thinking...</Text>
        </Box>
      )}
      <div ref={chatBottomRef} />
    </Box>
  );
}

interface UserInputAreaProps {
  input: string;
  handleInputChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  imagePreviews: string[];
  isSubmitting: boolean;
  handleSend: () => void;
  handleTakePhoto: () => void;
  handleUploadImage: () => void;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  handleImageChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  setImageFiles: (files: File[]) => void;
  setImagePreviews: (previews: string[]) => void;
  showChatOptions: boolean;
  isMobileDevice: boolean;
  removeImage: (index: number) => void;
}

const UserInputArea: React.FC<UserInputAreaProps> = ({
  input,
  handleInputChange,
  imagePreviews,
  isSubmitting,
  handleSend,
  handleTakePhoto,
  handleUploadImage,
  fileInputRef,
  handleImageChange,
  setImageFiles,
  setImagePreviews,
  showChatOptions,
  isMobileDevice,
  removeImage
}) => {
  if (showChatOptions) return null;
  
  return (
    <Box
      as="form"
      onSubmit={(e) => {
        e.preventDefault();
        handleSend();
      }}
      position="relative"
      width="100%"
      px={4}
      pb={4}
      mt="auto"
      display="flex"
      justifyContent="center"
      zIndex={20}
      pointerEvents="auto"
      background="transparent"
    >
      <Box
        width="100%"
        maxWidth="540px"
        background="#fff"
        borderRadius="xl"
        boxShadow="0 4px 24px rgba(0,0,0,0.10)"
        p={4}
        display="flex"
        flexDirection="column"
        gap={3}
      >
        <Textarea
          placeholder="Describe your DIY issue..."
          value={input}
          onChange={handleInputChange}
          resize="none"
          rows={2}
          maxHeight="160px"
          disabled={isSubmitting}
          background="var(--amber-50)"
          color="black"
          fontSize="lg"
          px={4}
          py={3}
          borderRadius="lg"
          _focus={{ borderColor: 'var(--brand-secondary)', boxShadow: '0 0 0 1px var(--brand-secondary)' }}
          className="diy-textarea"
        />
        
        {imagePreviews.length > 0 && (
          <Box display="flex" flexDirection="row" alignItems="center" gap={2} flexWrap="wrap">
            {imagePreviews.map((preview, index) => (
              <Box key={index} position="relative">
                <Image
                  src={preview}
                  alt={`Preview ${index + 1}`}
                  boxSize="64px"
                  borderRadius="8px"
                  objectFit="cover"
                />
                <IconButton
                  aria-label="Remove image"
                  as={FiX}
                  size="xs"
                  position="absolute"
                  top="-8px"
                  right="-8px"
                  borderRadius="full"
                  bg="var(--brand-secondary)"
                  color="white"
                  _hover={{ bg: 'var(--brand-dark)' }}
                  onClick={() => removeImage(index)}
                />
              </Box>
            ))}
          </Box>
        )}
        
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" gap={2}>
            {isMobileDevice && (
              <IconButton
                aria-label="Take photo"
                as={FiCamera}
                onClick={handleTakePhoto}
                disabled={isSubmitting}
                variant="ghost"
                color="black"
              />
            )}
            <IconButton
              aria-label="Upload image"
              as={FiImage}
              onClick={handleUploadImage}
              disabled={isSubmitting}
              variant="ghost"
              color="black"
            />
            <Input
              type="file"
              accept="image/*;capture=camera"
              onChange={handleImageChange}
              ref={fileInputRef}
              display="none"
              data-testid="file-input"
              capture="environment"
            />
          </Box>
          
          <Button
            onClick={handleSend}
            disabled={(!input.trim() && imagePreviews.length === 0) || isSubmitting}
            bg="var(--brand-secondary)"
            color="white"
            _hover={{ bg: 'var(--brand-dark)' }}
          >
            {isSubmitting ? 'Sending...' : 'Send'} {!isSubmitting && <FiSend style={{ marginLeft: '8px' }} />}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

const DIYBuddy: React.FC = () => {
  const navigate = useNavigate();
  const toaster = createToaster({
    placement: 'top',
  });
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [navigationState, setNavigationState] = useState<NavigationState>('issueOptions');
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [issueType, setIssueType] = useState<string>('diagnose'); // Default issue type
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false); // New state for feedback modal
  const fileInputRef = useRef<HTMLInputElement>(null);
  const chatBottomRef = useRef<HTMLDivElement>(null);
  // Colors from our amber palette
  const bgBubbleUser = 'var(--brand-secondary)'; // amber.500
  const bgBubbleAI = 'var(--amber-50)'; // amber.50

  // Track when DIY Buddy component mounts
  useEffect(() => {
    if (posthog && typeof posthog.capture === 'function') {
      posthog.capture('diy_buddy_opened', {
        page_name: 'DIY Buddy',
        initial_state: navigationState,
        timestamp: new Date().toISOString(),
        user_agent: navigator.userAgent,
        referrer: document.referrer
      });
    }
  }, []); // Empty dependency array means this runs once on mount

  // Effect to handle scrolling when messages change
  useEffect(() => {
    if (chatBottomRef.current) {
      chatBottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  useEffect(() => {
    // Check if device is mobile
    const checkMobileDevice = () => {
      // Check for touch capability and screen size
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || 
                      (('ontouchstart' in window) && window.innerWidth <= 768);
      setIsMobileDevice(isMobile);
      
      // Add class for mobile styling
      if (isMobile) {
        document.body.classList.add('has-diybuddy');
      }
    };
    
    checkMobileDevice();
    
    // Handle resize events
    window.addEventListener('resize', checkMobileDevice);
    
    return () => {
      document.body.classList.remove('has-diybuddy');
      window.removeEventListener('resize', checkMobileDevice);
    };
  }, []);
  
  const handleCloseApp = () => {
    navigate('/');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
  };


  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      // Check if adding this image would exceed the 5 image limit
      if (imagePreviews.length >= 5) {
        toaster.warning({
          title: 'Maximum Images Reached',
          description: 'You can only add up to 5 images per message.',
          duration: 3000,
        });
        return;
      }
      
      const originalFile = e.target.files[0];
      
      try {
        // Show loading state while resizing
        setIsSubmitting(true);
        
        // Resize the image
        const resizedFile = await resizeImage(originalFile);
        
        // Update state with resized image (add to existing images)
        setImageFiles(prevFiles => [...prevFiles, resizedFile]);
        setImagePreviews(prevPreviews => [...prevPreviews, URL.createObjectURL(resizedFile)]);
      } catch (error) {
        console.error('Error resizing image:', error);
        // Fallback to original file if resizing fails
        setImageFiles(prevFiles => [...prevFiles, originalFile]);
        setImagePreviews(prevPreviews => [...prevPreviews, URL.createObjectURL(originalFile)]);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleTakePhoto = async () => {
    // This method is primarily for mobile devices to take photos with the camera
    try {
      if (fileInputRef.current) {
        // Reset the file input to ensure it triggers properly
        fileInputRef.current.value = '';
        fileInputRef.current.accept = 'image/*';
        fileInputRef.current.setAttribute('capture', 'environment');
        fileInputRef.current.click();
      }
    } catch (err) {
      console.error('Error accessing camera:', err);
      // Show error toast to user
      toaster.error({
        title: 'Camera Access Failed',
        description: 'Unable to access the camera. Please check your permissions or try uploading an existing image instead.',
        duration: 5000,
      });
    }
  };

  const handleUploadImage = () => {
    if (fileInputRef.current) {
      fileInputRef.current.accept = 'image/*';
      // Remove capture attribute to allow gallery selection
      fileInputRef.current.removeAttribute('capture');
      fileInputRef.current.click();
    }
  };

  const handleOptionSelect = async (option: string) => {
    // Track the option selection in PostHog
    const optionLabel = DIY_OPTION_LABELS[option as keyof typeof DIY_OPTION_LABELS] || option;
    
    trackButtonClick(`DIY Option: ${optionLabel}`, 'DIY Buddy', {
      option_type: option,
      option_label: optionLabel,
      session_id: conversationId || 'new-session',
      timestamp: new Date().toISOString()
    });
    
    // Also track as a custom event for better analytics
    if (posthog && typeof posthog.capture === 'function') {
      posthog.capture('diy_option_selected', {
        option_type: option,
        option_label: optionLabel,
        page_name: 'DIY Buddy',
        session_id: conversationId || 'new-session',
        timestamp: new Date().toISOString()
      });
    }
    
    // Clear previous chat messages when a new option is selected
    setMessages([]);
    setIsSubmitting(true);
    setImageFiles([]);
    setImagePreviews([]);
    setInput('');
    
    // Reset conversation ID when starting a new chat
    setConversationId(null);
    
    try {
      // Map the option directly to the backend issue type
      let selectedIssueType: string = option;
      
      // For backward compatibility with existing conversations
      if (option === 'appliance-info') {
        selectedIssueType = 'applianceInstructions';
      }
      
      // Save the selected issue type for later use in chat requests
      setIssueType(selectedIssueType);
      
      const response = await axios.get(`/api/diy/issueOptions?issueType=${selectedIssueType}`);
      
      const aiMsg: ChatMessage = {
        id: `${Date.now()}-ai`,
        type: 'ai',
        text: response.data,
        createdAt: new Date().toISOString(),
      };
      
      setMessages([aiMsg]);
      setNavigationState('issueChat');
    } catch (error) {
      console.error('Error fetching issue options:', error);
      
      let fallbackResponse = '';
      switch(option) {
        case 'applianceInstructions':
          fallbackResponse = 'Please provide the make, model, or serial number of your appliance, or take a photo of it so I can help you with information or instructions.';
          break;
        case 'identifyPart':
          fallbackResponse = 'Please take a photo of the part you want to identify and add a brief description like "This bit in the photo..." to help me understand what you need.';
          break;
        case 'diagnose':
          fallbackResponse = 'Please describe the issue you\'re having. If it\'s an appliance, include the make, model, or serial number if available. Adding photos will help me diagnose the problem more accurately.';
          break;
        default:
          fallbackResponse = 'Please provide more details about what you need help with. Adding photos can help me assist you better.';
          break;
      }
      
      const fallbackMsg: ChatMessage = {
        id: `${Date.now()}-ai`,
        type: 'ai',
        text: fallbackResponse,
        createdAt: new Date().toISOString(),
        isMarkdown: false,
      };
      
      setMessages([fallbackMsg]);
      setNavigationState('issueChat');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackToOptions = () => {
    // Track navigation back to options
    trackButtonClick('Back to Options', 'DIY Buddy', {
      from_state: 'issueChat',
      to_state: 'issueOptions',
      session_id: conversationId || 'unknown',
      timestamp: new Date().toISOString()
    });
    
    if (posthog && typeof posthog.capture === 'function') {
      posthog.capture('diy_navigation_back', {
        from_state: 'issueChat',
        to_state: 'issueOptions',
        page_name: 'DIY Buddy',
        session_id: conversationId || 'unknown',
        timestamp: new Date().toISOString()
      });
    }
    
    setNavigationState('issueOptions');
  };

  const handleSend = async () => {
    if (!input.trim() && imageFiles.length === 0) {
      alert('Please enter a description or add a photo.');
      return;
    }
    
    // Track message sending
    if (posthog && typeof posthog.capture === 'function') {
      posthog.capture('diy_message_sent', {
        issue_type: issueType,
        has_text: !!input.trim(),
        has_image: imageFiles.length > 0,
        message_length: input.trim().length,
        session_id: conversationId || 'new-session',
        page_name: 'DIY Buddy',
        timestamp: new Date().toISOString()
      });
    }
    
    setIsSubmitting(true);
    
    // We'll let the backend generate a conversation ID if we don't have one yet
    
    // Create user message for UI
    const userMsg: ChatMessage = {
      id: `${Date.now()}-user`,
      type: 'user',
      text: input.trim() || undefined,
      imageUrls: imagePreviews.length > 0 ? imagePreviews : undefined,
      createdAt: new Date().toISOString(),
    };
    setMessages((msgs) => [...msgs, userMsg]);
    
    try {
      // Convert images to base64 if present
      const imagesBase64: string[] = [];
      
      if (imageFiles.length > 0) {
        // Process each image file
        for (const imageFile of imageFiles) {
          // Use existing resizeImage utility to resize the image
          const resizedFile = await resizeImage(imageFile, 2048);
          
          // Convert to base64
          const imageBase64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              const base64 = reader.result as string;
              // Extract just the base64 data part (remove the data:image/jpeg;base64, prefix)
              const base64Data = base64.split(',')[1];
              resolve(base64Data);
            };
            reader.onerror = reject;
            reader.readAsDataURL(resizedFile);
          });
          
          imagesBase64.push(imageBase64);
        }
      }
      
      // Call the new chat endpoint with conversation ID (if we have one), issue type, and base64 images
      // Format the chatAbout enum according to Vapor's Content protocol requirements
      const response = await axios.post('/api/diy/chat', {
        conversationId: conversationId, // Will be null on first message
        chatAbout: { [issueType]: {} }, // Format enum as a dictionary with case name as key
        message: input.trim(),
        images: imagesBase64.length > 0 ? imagesBase64 : undefined
      });
      
      // Store the conversation ID returned from the backend
      if (response.data.conversationId) {
        setConversationId(response.data.conversationId);
      }
      
      // Create AI response message for UI
      const aiMsg: ChatMessage = {
        id: `${Date.now()}-ai`,
        type: 'ai',
        text: response.data.message,
        createdAt: new Date().toISOString(),
        isMarkdown: true,
      };
      
      setMessages((msgs) => [...msgs, aiMsg]);
      setInput('');
      setImageFiles([]);
      setImagePreviews([]);
    } catch (error) {
      console.error('Error submitting issue:', error);
      
      const errorMsg: ChatMessage = {
        id: `${Date.now()}-ai`,
        type: 'ai',
        text: 'Sorry, there was an error submitting your issue. Please try again later.',
        createdAt: new Date().toISOString(),
        isMarkdown: false,
      };
      
      setMessages((msgs) => [...msgs, errorMsg]);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenFeedbackModal = () => {
    setIsFeedbackModalOpen(true);
  };

  const handleCloseFeedbackModal = () => {
    setIsFeedbackModalOpen(false);
  };

  return (
    <Box
      width={{ base: "100%", sm: "100%", md: "80%", lg: "600px" }}
      height={{ base: "100%", sm: "100%", md: "80vh", lg: "80vh" }}
      maxHeight={{ base: "100vh", sm: "100vh", md: "80vh" }}
      margin="0 auto"
      display="flex"
      flexDirection="column"
      background="var(--white)"
      position={{ base: "fixed", sm: "fixed", md: "relative" }}
      top={{ base: 0, sm: 0, md: "auto" }}
      left={{ base: 0, sm: 0, md: "auto" }}
      right={{ base: 0, sm: 0, md: "auto" }}
      bottom={{ base: 0, sm: 0, md: "auto" }}
      zIndex={{ base: 1000, sm: 1000, md: "auto" }}
      borderRadius={{ base: 0, sm: 0, md: "xl" }}
      boxShadow={{ base: "none", sm: "none", md: "0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)" }}
      overflow="hidden"
      border={{ base: "none", sm: "none", md: "1px solid var(--amber-100)" }}
      paddingBottom={{ base: "env(safe-area-inset-bottom, 0px)", md: "0" }}
    >
      {/* Header */}
      <Box
        width="100%"
        py={3}
        px={4}
        bg="var(--brand-secondary)"
        color="white"
        display="flex"
        alignItems="center"
        borderTopLeftRadius={{ base: 0, md: "xl" }}
        borderTopRightRadius={{ base: 0, md: "xl" }}
        bgGradient="linear(to-r, var(--brand-secondary), var(--brand-dark))"
      >
        {navigationState === 'issueChat' ? (
          <IconButton
            aria-label="Back to options"
            variant="ghost"
            color="white"
            onClick={handleBackToOptions}
            size="md"
            mr={2}
            _hover={{ bg: 'var(--brand-dark)' }}
          >
            <FiArrowLeft />
          </IconButton>
        ) : (
          <Box mr={2}>
            <FiMessageCircle size="24px" />
          </Box>
        )}
        <Text fontWeight="bold" fontSize="lg" flex={1}>DIY Buddy</Text>
        <IconButton
          aria-label="Close DIY Buddy"
          variant="ghost"
          color="white"
          onClick={handleCloseApp}
          size="md"
          _hover={{ bg: 'var(--brand-dark)' }}
        >
          <FiX />
        </IconButton>
      </Box>
      
      <Box height="1px" width="100%" bg="gray.200" />
      
      {/* Chat area */}
      <Box
        flex={1}
        display="flex"
        flexDirection="column"
        px={2}
        py={4}
        overflowY="auto"
        position="relative"
        mb={2}
      >
        {/* Show either ChatOptions or ChatHistory based on navigation state */}
        {navigationState === 'issueOptions' ? (
          <ChatOptions handleOptionSelect={handleOptionSelect} onOpenFeedbackModal={handleOpenFeedbackModal} />
        ) : (
          <ChatHistory 
            messages={messages.map(msg => {
              // For backward compatibility, convert single imageUrl to imageUrls array
              if (msg.imageUrl && !msg.imageUrls) {
                return {
                  ...msg,
                  imageUrls: [msg.imageUrl]
                };
              }
              return msg;
            })}
            isSubmitting={isSubmitting}
            chatBottomRef={chatBottomRef}
          />
        )}
      </Box>
      
      {/* Use UserInputArea component */}
      <UserInputArea
        input={input}
        handleInputChange={handleInputChange}
        imagePreviews={imagePreviews}
        isSubmitting={isSubmitting}
        handleSend={handleSend}
        handleTakePhoto={handleTakePhoto}
        handleUploadImage={handleUploadImage}
        fileInputRef={fileInputRef}
        handleImageChange={handleImageChange}
        setImageFiles={setImageFiles}
        setImagePreviews={setImagePreviews}
        showChatOptions={navigationState === 'issueOptions'}
        isMobileDevice={isMobileDevice}
        removeImage={(index) => {
          setImageFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
          setImagePreviews(prevPreviews => prevPreviews.filter((_, i) => i !== index));
        }}
      />
      
      {/* Feedback modal */}
      <FeedbackModal
        isOpen={isFeedbackModalOpen}
        onClose={handleCloseFeedbackModal}
      />
    </Box>
  );
};

export default DIYBuddy;
